import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import {
  assessRetouching,
  confidenceWord,
  DISCLAIMER,
  judgeRisk,
  RETOUCHING_WEIGHTS,
} from '../authenticity/index.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * «Ретушь и изменение внешности».
 *
 * The ensemble combines the skin-texture signal (D), the skin-smoothing signal
 * (E) and the two supporting pixel signals (A compression history, H clone).
 * The maximum status is `warning`: the validator never makes a legal claim
 * about a person and never fails a photo for suspected retouching by default.
 */
export const retouchingCheck: ValidationCheck = {
  id: 'retouching',
  title: 'Ретушь и изменение внешности',
  description:
    'Сравнивает текстуру и локальный контраст кожи с фоном и краями лица, ищет следы сглаживания, пересжатия и копирования фрагментов.',
  kind: 'content',
  requires: ['pixels', 'quality', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'quality', 'geometry']);
    if (missing) return notChecked(ctx, retouchingCheck, missing, ctx.rule.severity);

    const assessment = assessRetouching(ctx);
    const warnRisk = numberParam(ctx.rule.params, 'warnRisk', 0.4);
    const uncertainBelowConfidence = numberParam(
      ctx.rule.params,
      'uncertainBelowConfidence',
      0.45,
    );

    const risk = assessment.risk;
    const confidence = assessment.confidence;
    // A retouching suspicion can never fail a photo: only pass/warning/uncertain.
    const verdict = judgeRisk({
      risk,
      confidence,
      warnRisk,
      uncertainBelowConfidence,
      failConfidence: 1,
    });
    const status = verdict.status === 'fail' ? 'warning' : verdict.status;
    const word = confidenceWord(confidence);
    const findings =
      assessment.findings.length > 0 ? assessment.findings : ['явных признаков не обнаружено'];

    const message =
      status === 'pass'
        ? '✅ Признаков ретуши не обнаружено'
        : status === 'warning'
          ? '⚠ Обнаружены признаки изменения внешности'
          : 'Не удалось уверенно проверить признаки ретуши.';


    return plainResult({
      id: retouchingCheck.id,
      title: retouchingCheck.title,
      status,
      severity: ctx.rule.severity,
      message,
      fixability: 'retake',
      confidence: roundTo(confidence, 2),
      details: {
        risk: roundTo(risk, 4),
        confidence: roundTo(confidence, 4),
        confidenceWord: word,
        signals: assessment.signals.map((signal) => ({
          id: signal.id,
          score: roundTo(signal.score, 4),
          confidence: roundTo(signal.confidence, 4),
          note: signal.note,
        })),
        findings,
        unavailable: assessment.unavailable,
        weights: { ...RETOUCHING_WEIGHTS },
        thresholds: { warnRisk, uncertainBelowConfidence, failRisk: null, failConfidence: null },
        verdictReason: verdict.reason,
        disclaimer: DISCLAIMER,
      },
    });
  },
};
